import PropTypes from "prop-types";
import { useCallback, useState } from "react";
import { convertMilliSecondsToTime } from "app/utils/utils";
import AmountContainer from "app/pages/.shared/AmountContainer";
import {
	flightsFilterValuesShape,
	quotationFlightShape,
	quotationPropTypes,
} from "app/utils/propTypes";
import { FormattedMessage, injectIntl } from "react-intl";
import classNames from "classnames";
import isEqual from "lodash/isEqual";
import CabinsFilter from "./CabinsFilter.jsx";
import FilterPredicates from "./FilterPredicates.jsx";
import { sendTagOnFlightFilter, sendTagOnFlightResetFilter } from "app/utils/analytics";
import RangeInput from "app/pages/.shared/form/RangeInput.jsx";
import "./FlightsFilterSort.scss";
import FlightsFilterMarketingBannerContainer from "app/pages/Booking/Quotation/Flight/filter/FlightsFilterMarketingBannerContainer";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import FlightFilterButton from "./FlightFilterButton.jsx";
import Button, { BUTTON_SIZES, BUTTON_VARIANTS } from "app/pages/.shared/form/Button";

const FILTER_KEYS = {
	DEPARTURE_AIRPORT: "departureAirport",
	ARRIVAL_AIRPORT: "arrivalAirport",
	CABIN: "cabin",
	AIRLINE: "airline",
	LUGGAGE: "luggage",
	STOP: "stop",
	INBOUND_DURATION: "inboundDuration",
	OUTBOUND_DURATION: "outboundDuration",
	INBOUND_TAKE_OFF_TIME: "inboundTakeOffTime",
	OUTBOUND_TAKE_OFF_TIME: "outboundTakeOffTime",
	UPGRADE_PRICE: "upgradePrice",
};

const FlightsFilterSort = ({
	travelByTrainAvailable,
	quotation,
	filterValues,
	setFlightsFilterBy,
	intl,
	filtersCount,
	resetFlightsFilter,
	resetFlightsPageSize,
	minUpgradePricesByCabin,
	includedFlight,
	shouldShowFlightFilters,
	currencyCode,
}) => {
	const [selectedCabin, setSelectedCabin] = useState(undefined);
	const filterBy = useCallback(predicate => setFlightsFilterBy(predicate), []);

	const onClickCabin = useCallback(cabin => {
		let predicate = { [FILTER_KEYS.CABIN]: cabin };
		filterBy(predicate);
		sendTagOnFlightFilter(FILTER_KEYS.CABIN, cabin);
		setSelectedCabin(cabin);
		resetFlightsPageSize();
	}, []);

	const filterAirportButtonClassName = classNames({
		action__item: true,
		"action__item--active":
			quotation.filterFlightsBy[FILTER_KEYS.DEPARTURE_AIRPORT] !== undefined ||
			quotation.filterFlightsBy[FILTER_KEYS.ARRIVAL_AIRPORT] !== undefined,
	});

	const filterAirlineButtonClassName = classNames({
		action__item: true,
		"action__item--active": quotation.filterFlightsBy[FILTER_KEYS.AIRLINE] !== undefined,
	});

	const filterStopButtonClassName = classNames({
		action__item: true,
		"action__item--active": quotation.filterFlightsBy[FILTER_KEYS.STOP] !== undefined,
	});

	const filterLuggageButtonClassName = classNames({
		action__item: true,
		"action__item--active": quotation.filterFlightsBy[FILTER_KEYS.LUGGAGE] !== undefined,
	});

	const filterDurationButtonClassName = classNames({
		action__item: true,
		"action__item--active":
			quotation.filterFlightsBy[FILTER_KEYS.INBOUND_DURATION]?.min !== undefined &&
			quotation.filterFlightsBy[FILTER_KEYS.INBOUND_DURATION]?.max !== undefined &&
			quotation.filterFlightsBy[FILTER_KEYS.OUTBOUND_DURATION]?.min !== undefined &&
			quotation.filterFlightsBy[FILTER_KEYS.OUTBOUND_DURATION]?.max !== undefined &&
			(!isEqual(
				quotation.filterFlightsBy[FILTER_KEYS.INBOUND_DURATION],
				filterValues[FILTER_KEYS.INBOUND_DURATION]
			) ||
				!isEqual(
					quotation.filterFlightsBy[FILTER_KEYS.OUTBOUND_DURATION],
					filterValues[FILTER_KEYS.OUTBOUND_DURATION]
				)),
	});

	const filterTakeOffTimeButtonClassName = classNames({
		action__item: true,
		"action__item--active":
			quotation.filterFlightsBy[FILTER_KEYS.INBOUND_TAKE_OFF_TIME] !== undefined &&
			quotation.filterFlightsBy[FILTER_KEYS.OUTBOUND_TAKE_OFF_TIME] !== undefined &&
			(!isEqual(
				quotation.filterFlightsBy[FILTER_KEYS.INBOUND_TAKE_OFF_TIME],
				filterValues[FILTER_KEYS.INBOUND_TAKE_OFF_TIME]
			) ||
				!isEqual(
					quotation.filterFlightsBy[FILTER_KEYS.OUTBOUND_TAKE_OFF_TIME],
					filterValues[FILTER_KEYS.OUTBOUND_TAKE_OFF_TIME]
				)),
	});

	const filterPriceButtonClassName = classNames({
		action__item: true,
		"action__item--active":
			quotation.filterFlightsBy[FILTER_KEYS.UPGRADE_PRICE] !== undefined &&
			quotation.filterFlightsBy[FILTER_KEYS.UPGRADE_PRICE] !==
				filterValues[FILTER_KEYS.UPGRADE_PRICE],
	});

	// Le composant react-popover ne bénéficie pas de react-intl. On doit donc générer les labels en dehors

	const getStopFilterLabel = stop => {
		if (stop === 0) {
			return intl.formatMessage({ id: "general.direct" });
		} else if (intl.formatPlural(stop) === "one") {
			return intl.formatMessage(
				{
					id: "general.stop.singular",
				},
				{
					value: stop,
				}
			);
		}

		return intl.formatMessage(
			{
				id: "general.stop.plural",
			},
			{
				value: stop,
			}
		);
	};

	const getLuggageFilterLabel = isLuggageIncluded => {
		if (isLuggageIncluded === true) {
			return intl.formatMessage({ id: "general.luggage.included" });
		}
		return intl.formatMessage({ id: "general.luggage.not.included" });
	};

	const shouldShowInboundDurationFilter =
		filterValues.inboundDuration.min !== filterValues.inboundDuration.max;
	const shouldShowOutboundDurationFilter =
		filterValues.outboundDuration.min !== filterValues.outboundDuration.max;
	const shouldShowDurationFilter =
		shouldShowInboundDurationFilter && shouldShowOutboundDurationFilter;

	const shouldShowOutboundTimeFilter =
		filterValues.outboundTakeOffTime.min !== filterValues.outboundTakeOffTime.max;
	const shouldShowInboundTimeFilter =
		filterValues.inboundTakeOffTime.min !== filterValues.inboundTakeOffTime.max;
	const shouldShowTimeFilter = shouldShowOutboundTimeFilter && shouldShowInboundTimeFilter;

	const shouldShowPriceFilter = filterValues.upgradePrice !== 0;

	const createAirportFilter = options => {
		const { filterKey, values = [], activeValue, labelIds = {} } = options || {};

		if (!values || values.length <= 1) {
			return null;
		}

		const labelId = travelByTrainAvailable ? labelIds.train : labelIds.default;

		return (
			<FilterPredicates
				key={filterKey}
				label={intl.formatMessage({ id: labelId })}
				predicates={values}
				activeIfEqualTo={activeValue}
				onSelect={selectedValue => {
					const isActive = selectedValue === activeValue;
					const predicate = isActive ? undefined : selectedValue;
					filterBy({ [filterKey]: predicate });

					if (!isActive) {
						sendTagOnFlightFilter(filterKey, predicate);
					}
				}}
			/>
		);
	};

	const airportFilters = [
		createAirportFilter({
			filterKey: FILTER_KEYS.DEPARTURE_AIRPORT,
			values: filterValues?.departureAirport,
			activeValue: quotation.filterFlightsBy.departureAirport,
			labelIds: {
				train: "general.filter.departure.airport.gare.label",
				default: "general.filter.departure.airport.label",
			},
		}),
		createAirportFilter({
			filterKey: FILTER_KEYS.ARRIVAL_AIRPORT,
			values: filterValues?.arrivalAirport,
			activeValue: quotation.filterFlightsBy.arrivalAirport,
			labelIds: {
				train: "general.filter.arrival.airport.gare.label",
				default: "general.filter.arrival.airport.label",
			},
		}),
	].filter(Boolean);

	const shouldShowAirportFilter = airportFilters.length > 0;
	const shouldGroupAirportFilters = airportFilters.length > 1;
	const airportButtonLabelId = travelByTrainAvailable
		? "quotation.filter.multi.transport"
		: "quotation.flights.filter.airport";
	const upgradePriceLabel = intl.formatMessage({ id: "general.filter.upgrade.price.label" });

	const durationFilterContent = (
		<div className="filter-predicates filter-predicates--slider">
			{shouldShowOutboundDurationFilter && (
				<div className="filter-predicates__line">
					<div className="filter-predicates__min">
						<Typography variant={TYPOGRAPHY_VARIANTS.REGULAR}>
							{convertMilliSecondsToTime(filterValues.outboundDuration.min)}
						</Typography>
					</div>
					<RangeInput
						label={intl.formatMessage({ id: "general.filter.outbound.duration.label" })}
						defaultValue={[
							quotation.filterFlightsBy.outboundDuration?.min ??
								filterValues.outboundDuration.min,
							quotation.filterFlightsBy.outboundDuration?.max ??
								filterValues.outboundDuration.max,
						]}
						step={1}
						tipFormatter={convertMilliSecondsToTime}
						min={filterValues.outboundDuration.min}
						max={filterValues.outboundDuration.max}
						onValueChange={value => {
							const [minValue, maxValue] = value;
							filterBy({
								[FILTER_KEYS.OUTBOUND_DURATION]: {
									min: minValue,
									max: maxValue,
								},
							});

							sendTagOnFlightFilter(
								FILTER_KEYS.OUTBOUND_DURATION,
								`${convertMilliSecondsToTime(
									minValue
								)} - ${convertMilliSecondsToTime(maxValue)}`
							);
						}}
					/>
					<div className="filter-predicates__max">
						<Typography variant={TYPOGRAPHY_VARIANTS.REGULAR}>
							{convertMilliSecondsToTime(filterValues.outboundDuration.max)}
						</Typography>
					</div>
				</div>
			)}

			{shouldShowInboundDurationFilter && (
				<div className="filter-predicates__line">
					<div className="filter-predicates__min">
						<Typography variant={TYPOGRAPHY_VARIANTS.REGULAR}>
							{convertMilliSecondsToTime(filterValues.inboundDuration.min)}
						</Typography>
					</div>
					<RangeInput
						label={intl.formatMessage({ id: "general.filter.inbound.duration.label" })}
						defaultValue={[
							quotation.filterFlightsBy.inboundDuration?.min ??
								filterValues.inboundDuration.min,
							quotation.filterFlightsBy.inboundDuration?.max ??
								filterValues.inboundDuration.max,
						]}
						step={1}
						tipFormatter={convertMilliSecondsToTime}
						min={filterValues.inboundDuration.min}
						max={filterValues.inboundDuration.max}
						onValueChange={value => {
							const [minValue, maxValue] = value;
							filterBy({
								[FILTER_KEYS.INBOUND_DURATION]: {
									min: minValue,
									max: maxValue,
								},
							});

							sendTagOnFlightFilter(
								FILTER_KEYS.INBOUND_DURATION,
								`${convertMilliSecondsToTime(
									minValue
								)} - ${convertMilliSecondsToTime(maxValue)}`
							);
						}}
					/>
					<div className="filter-predicates__max">
						<Typography variant={TYPOGRAPHY_VARIANTS.REGULAR}>
							{convertMilliSecondsToTime(filterValues.inboundDuration.max)}
						</Typography>
					</div>
				</div>
			)}
		</div>
	);

	const timeFilterContent = (
		<div className="filter-predicates filter-predicates--slider">
			{shouldShowOutboundTimeFilter && (
				<div className="filter-predicates__line">
					<div className="filter-predicates__min">
						<Typography variant={TYPOGRAPHY_VARIANTS.REGULAR}>
							{convertMilliSecondsToTime(filterValues.outboundTakeOffTime.min)}
						</Typography>
					</div>
					<RangeInput
						label={
							<span className="filter-sort__time-label">
								<span className="filter-sort__time-FormattedMessage-title">
									<FormattedMessage id="quotation.flight.outbound.title" />
								</span>
								<span className="filter-sort__time-label-suffix">
									{" ("}
									<FormattedMessage id="general.filter.time.label" />)
								</span>
							</span>
						}
						defaultValue={[
							quotation.filterFlightsBy.outboundTakeOffTime.min,
							quotation.filterFlightsBy.outboundTakeOffTime.max,
						]}
						step={1}
						tipFormatter={convertMilliSecondsToTime}
						min={filterValues.outboundTakeOffTime.min}
						max={filterValues.outboundTakeOffTime.max}
						onValueChange={value => {
							filterBy({
								[FILTER_KEYS.OUTBOUND_TAKE_OFF_TIME]: {
									min: value[0],
									max: value[1],
								},
							});

							sendTagOnFlightFilter(
								FILTER_KEYS.OUTBOUND_TAKE_OFF_TIME,
								`${convertMilliSecondsToTime(
									value[0]
								)} - ${convertMilliSecondsToTime(value[1])}`
							);
						}}
					/>
					<div className="filter-predicates__max">
						<Typography variant={TYPOGRAPHY_VARIANTS.REGULAR}>
							{convertMilliSecondsToTime(filterValues.outboundTakeOffTime.max)}
						</Typography>
					</div>
				</div>
			)}

			{shouldShowInboundTimeFilter && (
				<div className="filter-predicates__line">
					<div className="filter-predicates__min">
						<Typography variant={TYPOGRAPHY_VARIANTS.REGULAR}>
							{convertMilliSecondsToTime(filterValues.inboundTakeOffTime.min)}
						</Typography>
					</div>
					<RangeInput
						label={
							<span className="filter-sort__time-label">
								<span className="filter-sort__time-label-title">
									<FormattedMessage id="quotation.flight.inbound.title" />
								</span>
								<span className="filter-sort__time-label-suffix">
									{" ("}
									<FormattedMessage id="general.filter.time.label" />)
								</span>
							</span>
						}
						defaultValue={[
							quotation.filterFlightsBy.inboundTakeOffTime.min,
							quotation.filterFlightsBy.inboundTakeOffTime.max,
						]}
						step={1}
						tipFormatter={convertMilliSecondsToTime}
						min={filterValues.inboundTakeOffTime.min}
						max={filterValues.inboundTakeOffTime.max}
						onValueChange={value => {
							filterBy({
								[FILTER_KEYS.INBOUND_TAKE_OFF_TIME]: {
									min: value[0],
									max: value[1],
								},
							});

							sendTagOnFlightFilter(
								FILTER_KEYS.INBOUND_TAKE_OFF_TIME,
								`${convertMilliSecondsToTime(
									value[0]
								)} - ${convertMilliSecondsToTime(value[1])}`
							);
						}}
					/>
					<div className="filter-predicates__max">
						<Typography variant={TYPOGRAPHY_VARIANTS.REGULAR}>
							{convertMilliSecondsToTime(filterValues.inboundTakeOffTime.max)}
						</Typography>
					</div>
				</div>
			)}
		</div>
	);

	const airportFilterContent = shouldGroupAirportFilters ? (
		<div className="filter-sort__airport-group">{airportFilters}</div>
	) : (
		airportFilters[0] ?? null
	);

	const filtersLabel = intl.formatMessage({ id: "general.filter.label" });
	let displayedFiltersLabel = filtersLabel;
	if (filtersCount > 0) {
		displayedFiltersLabel = `${filtersLabel} (${filtersCount})`;
	}

	return (
		<div className="filter-sort">
			<FlightsFilterMarketingBannerContainer
				className="filter-sort__cabin-banner"
				onClickCabin={onClickCabin}
			/>
			{travelByTrainAvailable ? (
				<FormattedMessage id="quotation.transport.subtitle">
					{message => (
						<Typography
							variant={TYPOGRAPHY_VARIANTS.LARGE}
							isBold
							className="filter-sort__subtitle"
							component="h3"
						>
							{message}
						</Typography>
					)}
				</FormattedMessage>
			) : (
				<FormattedMessage id="quotation.flight.subtitle">
					{message => (
						<Typography
							variant={TYPOGRAPHY_VARIANTS.LARGE}
							isBold
							className="filter-sort__subtitle"
							component="h3"
						>
							{message}
						</Typography>
					)}
				</FormattedMessage>
			)}
			{shouldShowFlightFilters ? (
				<div className="filter-sort__buttons">
					<div className="filter-sort__filter">
						<Typography
							variant={TYPOGRAPHY_VARIANTS.REGULAR}
							className="filter-sort__counter"
						>
							{displayedFiltersLabel}
						</Typography>
					</div>

					{filtersCount > 0 ? (
						<Button
							variant={BUTTON_VARIANTS.BORDERLESS}
							size={BUTTON_SIZES.SMALL}
							label={
								<Typography
									variant={TYPOGRAPHY_VARIANTS.REGULAR}
									isBold
									className="action__reset-label"
								>
									{intl.formatMessage({ id: "flight.filter.sort.reset" })}
								</Typography>
							}
							onClick={() => {
								resetFlightsFilter(filterValues);
								resetFlightsPageSize();
								sendTagOnFlightResetFilter();
							}}
							className="action__reset"
						/>
					) : (
						false
					)}
				</div>
			) : (
				false
			)}

			{shouldShowFlightFilters ? (
				<div className="filter-sort__actions">
					<div className="action__list">
						{filterValues?.stopsCount?.length > 1 && (
							<FlightFilterButton
								className={filterStopButtonClassName}
								label={intl.formatMessage({ id: "quotation.flights.filter.stop" })}
								floatingContent={
									<FilterPredicates
										predicates={filterValues.stopsCount}
										defaultValue={2}
										activeIfEqualTo={quotation.filterFlightsBy.stop}
										customiseLabelFunction={getStopFilterLabel}
										onSelect={stop => {
											const isActive =
												stop === quotation.filterFlightsBy.stop;
											const predicate = isActive ? undefined : stop;
											filterBy({ [FILTER_KEYS.STOP]: predicate });
											sendTagOnFlightFilter(FILTER_KEYS.STOP, predicate);
										}}
									/>
								}
								floatingOffsetMobile={[0, -10]}
							/>
						)}
						{shouldShowDurationFilter && (
							<FlightFilterButton
								className={filterDurationButtonClassName}
								label={intl.formatMessage({
									id: "quotation.flights.filter.duration",
								})}
								floatingContent={durationFilterContent}
								dismissOnFloatingClick={false}
							/>
						)}

						{shouldShowTimeFilter && (
							<FlightFilterButton
								className={filterTakeOffTimeButtonClassName}
								label={intl.formatMessage({ id: "quotation.flights.filter.time" })}
								floatingContent={timeFilterContent}
								dismissOnFloatingClick={false}
							/>
						)}

						{filterValues.airline && filterValues.airline.length > 1 ? (
							<FlightFilterButton
								className={filterAirlineButtonClassName}
								label={intl.formatMessage({
									id: "quotation.flights.filter.airline",
								})}
								floatingContent={
									<FilterPredicates
										predicates={filterValues.airline}
										activeIfEqualTo={quotation.filterFlightsBy.airline}
										onSelect={airline => {
											const isActive =
												airline === quotation.filterFlightsBy.airline;
											const predicate = isActive ? undefined : airline;
											filterBy({ [FILTER_KEYS.AIRLINE]: predicate });

											if (!isActive) {
												sendTagOnFlightFilter(
													FILTER_KEYS.AIRLINE,
													predicate
												);
											}
										}}
									/>
								}
								floatingOffsetMobile={[0, -10]}
							/>
						) : (
							false
						)}

						{filterValues[FILTER_KEYS.LUGGAGE]?.length === 2 && (
							<FlightFilterButton
								className={filterLuggageButtonClassName}
								label={intl.formatMessage({
									id: "quotation.flights.filter.luggage",
								})}
								floatingContent={
									<FilterPredicates
										predicates={filterValues[FILTER_KEYS.LUGGAGE]}
										activeIfEqualTo={quotation.filterFlightsBy.luggage}
										customiseLabelFunction={getLuggageFilterLabel}
										onSelect={luggage => {
											const isActive =
												luggage === quotation.filterFlightsBy.luggage;
											const predicate = isActive ? undefined : luggage;
											filterBy({ [FILTER_KEYS.LUGGAGE]: predicate });

											if (!isActive) {
												sendTagOnFlightFilter(
													FILTER_KEYS.LUGGAGE,
													predicate
												);
											}
										}}
									/>
								}
								floatingOffsetMobile={[0, -10]}
							/>
						)}
					</div>
					<div className="action__list">
						{shouldShowPriceFilter && (
							<FlightFilterButton
								className={filterPriceButtonClassName}
								label={intl.formatMessage({ id: "quotation.flights.filter.price" })}
								floatingContent={
									<div className="filter-predicates filter-predicates--slider">
										<div className="filter-predicates__line">
											<div className="filter-predicates__min">
												<Typography variant={TYPOGRAPHY_VARIANTS.REGULAR}>
													<AmountContainer
														amount={0}
														amountClassName="filter-sort__amount"
														currency={currencyCode}
													/>
												</Typography>
											</div>
											<RangeInput
												label={upgradePriceLabel}
												range={false}
												defaultValue={
													quotation.filterFlightsBy.upgradePrice
												}
												step={1}
												min={0}
												tipFormatter={value => (
													<AmountContainer
														amount={value}
														amountClassName="filter-sort__amount"
														currency={currencyCode}
													/>
												)}
												max={filterValues.upgradePrice}
												onValueChange={value => {
													filterBy({
														[FILTER_KEYS.UPGRADE_PRICE]: value,
													});

													sendTagOnFlightFilter(
														FILTER_KEYS.UPGRADE_PRICE,
														value
													);
												}}
											/>
											<div className="filter-predicates__max">
												<Typography variant={TYPOGRAPHY_VARIANTS.REGULAR}>
													<AmountContainer
														amount={filterValues.upgradePrice}
														amountClassName="filter-sort__amount"
														currency={currencyCode}
													/>
												</Typography>
											</div>
										</div>
									</div>
								}
								dismissOnFloatingClick={false}
							/>
						)}

						{shouldShowAirportFilter && (
							<FlightFilterButton
								className={filterAirportButtonClassName}
								label={intl.formatMessage({ id: airportButtonLabelId })}
								floatingContent={airportFilterContent}
							/>
						)}
					</div>
				</div>
			) : (
				false
			)}

			{filterValues.cabin && filterValues.cabin.length > 1 && (
				<div className="filter-sort__cabins">
					<CabinsFilter
						includedFlight={includedFlight}
						minUpgradePricesByCabin={minUpgradePricesByCabin}
						cabins={filterValues.cabin}
						selectedCabin={selectedCabin || quotation.filterFlightsBy.cabin}
						onSelect={onClickCabin}
					/>
				</div>
			)}
		</div>
	);
};

FlightsFilterSort.defaultProps = {
	filtersCount: 0,
};

FlightsFilterSort.propTypes = {
	travelByTrainAvailable: PropTypes.bool,
	intl: PropTypes.object,
	currencyCode: PropTypes.string,
	includedFlight: quotationFlightShape,
	filterValues: flightsFilterValuesShape,
	filtersCount: PropTypes.number,
	quotation: quotationPropTypes,
	resetFlightsPageSize: PropTypes.func,
	setFlightsFilterBy: PropTypes.func,
	resetFlightsFilter: PropTypes.func,
	shouldShowFlightFilters: PropTypes.bool,
	minUpgradePricesByCabin: PropTypes.object,
};

export default injectIntl(FlightsFilterSort);
